import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core'
import { EditOutlined, Delete, Add, MailOutline } from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import { fetchAllOrganizers, deleteOrganizer, resendActivationEmail } from 'store/Organizers'
import OrganizerFormModal from './OrganizerFormModal'
import { fetchConference } from 'store/conference'

const tableHead = ['Full Name', 'Email', 'Status', 'Invited', 'Expires', 'Actions']

const formatInviteDate = (ms) => (ms ? moment(ms).format('MMM D, YYYY h:mm A') : '—')

const isInviteExpired = (org) => Boolean(org.inviteExpiresAt) && Date.now() > org.inviteExpiresAt

const getStatusChipProps = (org) => {
  if (org.mustChangePassword) {
    return { label: 'Pending Activation', backgroundColor: '#f59e0b' }
  }
  return org.status === 'in-active'
    ? { label: 'Inactive', backgroundColor: '#f44336' }
    : { label: 'Active', backgroundColor: '#4caf50' }
}

const formatTableData = (organizers, onEdit, onDelete) =>
  organizers.map((org) => {
    const { label, backgroundColor } = getStatusChipProps(org)
    return [
      org.fullName || '—',
      org.email,
      <Chip key={org.uid} label={label} size="small" style={{ backgroundColor, color: '#fff' }} />,
      formatInviteDate(org.inviteSentAt),
      formatInviteDate(org.inviteExpiresAt),
      <ActionButtons key={org.uid} organizer={org} onEdit={onEdit} onDelete={onDelete} />
    ]
  })

const ActionButtons = ({ organizer, onEdit, onDelete }) => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const [resending, setResending] = useState(false)
  const [openResendModal, setOpenResendModal] = useState(false)

  const handleResendActivation = async () => {
    setResending(true)
    await dispatch(resendActivationEmail({ uid: organizer.uid, callerUid: user?.id }))
    setResending(false)
    setOpenResendModal(false)
  }

  const expired = isInviteExpired(organizer)

  return (
    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
      {organizer.mustChangePassword && (
        <>
          <ConfirmModal
            dialogTitle="Resend Activation Email"
            dialogText={`This will invalidate the current activation link and send ${
              organizer.fullName || 'this organizer'
            } a brand-new temporary password and link. Continue?`}
            open={openResendModal}
            inProgress={resending}
            closeModal={() => setOpenResendModal(false)}
            handleConfirm={handleResendActivation}
          />
          <Tooltip
            title={
              expired
                ? 'Resend Activation Email'
                : `Invitation still valid until ${formatInviteDate(
                    organizer.inviteExpiresAt
                  )} — resend becomes available once it expires`
            }>
            <span>
              <IconButton size="small" disabled={!expired} onClick={() => setOpenResendModal(true)}>
                <MailOutline />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => onEdit(organizer)}>
          <EditOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => onDelete(organizer)}>
          <Delete />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const OrganizerListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const options = getTableOptions()

  const { organizers, inProgress } = useSelector((state) => state.organizers)
  const { conferences } = useSelector((state) => state.conference)

  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    dispatch(fetchAllOrganizers())
    dispatch(fetchConference())
  }, [dispatch])

  const getConferenceCount = (uid) => conferences.filter((c) => c.organizerId === uid).length

  const handleEdit = (organizer) => {
    setEditTarget(organizer)
    setFormOpen(true)
  }

  const handleDelete = (organizer) => {
    setDeleteTarget(organizer)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    await dispatch(deleteOrganizer(deleteTarget.uid))
    setDeleteLoading(false)
    setDeleteTarget(null)
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setEditTarget(null)
    dispatch(fetchAllOrganizers())
  }

  const confCount = deleteTarget ? getConferenceCount(deleteTarget.uid) : 0
  const deleteText =
    confCount > 0
      ? `Are you sure you want to delete "${deleteTarget?.fullName}"? They are linked to ${confCount} conference(s). Those conferences will be delinked from this organizer but will not be deleted.`
      : `Are you sure you want to delete "${deleteTarget?.fullName}"? This action cannot be undone.`

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h5" className={classes.headerTitle}>
          Conference Organizers
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => {
            setEditTarget(null)
            setFormOpen(true)
          }}>
          Add Organizer
        </Button>
      </Box>

      <Wrapper inProgress={inProgress}>
        <Paper variant="outlined" style={{ marginTop: '20px' }}>
          {organizers.length ? (
            <BasicTable
              data={formatTableData(organizers, handleEdit, handleDelete)}
              columns={tableHead}
              options={options}
            />
          ) : (
            <ResourceNotFound message="No conference organizers found." />
          )}
        </Paper>
      </Wrapper>

      <OrganizerFormModal open={formOpen} onClose={handleFormClose} organizer={editTarget} />

      <ConfirmModal
        dialogTitle="Delete Conference Organizer"
        dialogText={deleteText}
        open={Boolean(deleteTarget)}
        inProgress={deleteLoading}
        closeModal={() => setDeleteTarget(null)}
        handleConfirm={handleConfirmDelete}
      />
    </Box>
  )
}

export default OrganizerListing

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  headerTitle: {
    fontWeight: 600,
    color: '#263238'
  }
})
